class AutoScrollGallery extends HTMLElement {
  constructor() {
    super();
    this.splide = null;
    this.fullscreenSplide = null;
    this.speed = Number.parseInt(this.dataset.speed) || 1;
    this.isMobile = window.innerWidth < 1024;
    this.dialog = null;
    this._lastTrigger = null; // Track what triggered the modal
    this._keyboardOpen = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector('.fullscreen-carousel-dialog');
    
    this.setupDialogEvents();

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      this.waitForSplide();
    }, 100);
  }

  disconnectedCallback() {
    if (this.splide) {
      this.splide.destroy();
    }
    if (this.fullscreenSplide) {
      this.fullscreenSplide.destroy();
    }

    // Remove dialog event listeners
    if (this.dialog) {
      const closeButton = this.dialog.querySelector('.fullscreen-carousel-dialog__close');
      if (closeButton) {
        closeButton.removeEventListener('click', this.closeDialog.bind(this));
      }
      this.dialog.removeEventListener('click', this.handleDialogClick.bind(this));
      this.dialog.removeEventListener('close', this.handleDialogClose.bind(this));
    }
  }

  setupDialogEvents() {
    if (!this.dialog) {
      return;
    }
    // Set up click events on slide buttons to open dialog
    const slideButtons = this.querySelectorAll('.auto-scroll-gallery__item-button');
    slideButtons.forEach((button, index) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this._lastTrigger = button;
        this._keyboardOpen = false;
        this.openDialog(index);
      });
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          e.stopPropagation();
          this._lastTrigger = button;
          this._keyboardOpen = true;
          this.openDialog(index);
        }
      });
    });

    // Set up close button
    const closeButton = this.dialog.querySelector('.fullscreen-carousel-dialog__close');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => this.closeDialog(e));
    } 

    // Add click event to close dialog when clicking anywhere inside it
    this.dialog.addEventListener('click', this.handleDialogClick.bind(this));

    // Handle dialog close event
    this.dialog.addEventListener('close', this.handleDialogClose.bind(this));

    // Prevent pagination clicks from closing the dialog
    const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
    if (paginationContainer) {
      paginationContainer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }

  setupModalArrowNavigation() {
    // Instead of handling arrow keys ourselves, let Splide handle the navigation
    // and we'll just move focus if it's on an inactive indicator
    if (!this.fullscreenSplide) return;

    this.fullscreenSplide.on('moved', (newIndex) => {
      this.moveFocusFromInactiveIndicator();
    });
  }

  moveFocusFromInactiveIndicator() {
    const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
    if (!paginationContainer) {
      return;
    }
    
    // Check if any pagination button has focus-visible
    const focusedPaginationButton = paginationContainer.querySelector('.pagination-number:focus-visible');
    
    if (!focusedPaginationButton) {
      return;
    }
    
    // If there's a focus-visible pagination button, move focus to the active one
    const activePaginationButton = paginationContainer.querySelector('.pagination-number.active');
    if (activePaginationButton) {
      activePaginationButton.focus();
    }
  }

  waitForSplide() {
    // Check if Splide and AutoScroll extension are available
    if (typeof Splide !== 'undefined' && this.isSplideAutoScrollLoaded()) {
      this.initSplide();
      return;
    }
    // Set up an interval to check for Splide and AutoScroll
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (typeof Splide !== 'undefined' && this.isSplideAutoScrollLoaded()) {
        clearInterval(checkInterval);
        this.initSplide();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        this.initFallback();
      }
    }, 100);
  }

  isSplideAutoScrollLoaded() {
    // For CDN usage, check window.splide.Extensions
    const available = typeof Splide !== 'undefined' && 
           typeof window.splide !== 'undefined' && 
           typeof window.splide.Extensions !== 'undefined';
    
    return available;
  }

  initFallback() {
    // Try to initialize basic Splide without AutoScroll
    if (typeof Splide !== 'undefined') {
      this.initBasicSplide();
    } 
  }

  initBasicSplide() {
    try {
      this.splide = new Splide(this, {
        type: 'loop',
        drag: 'free',
        focus: 0,
        perPage: 'auto',
        perMove: 1,
        gap: 0,
        pagination: false,
        arrows: false,
        keyboard: true,
        drag: true,
      });

      this.splide.mount();
    } catch (error) {
    }
  }

  initSplide() {
    // Check DOM structure
    const track = this.querySelector('.splide__track');
    const list = this.querySelector('.splide__list');
    const slides = this.querySelectorAll('.splide__slide');
    
    if (!track || !list || slides.length === 0) {
      return;
    }

    // Calculate speed - convert from 1-100 scale to pixels per frame
    // Splide AutoScroll speed is pixels per frame, default is 1
    const normalizedSpeed = (this.speed / 100) * 3; // Scale to 0.03-3 range
    
    try {
      // Calculate clones needed for smooth looping with AutoScroll
      const slideCount = slides.length;
      const calculatedClones = Math.max(slideCount, 6); // Ensure at least 6 clones for smooth looping
      
      // Initialize main auto-scroll gallery
      const config = {
        type: 'loop',
        drag: 'free',
        focus: 0,
        perPage: 'auto', // Auto-fit slides based on their width
        perMove: 1,
        gap: 0,
        pagination: false,
        arrows: false,
        clones: calculatedClones, // Dynamically calculated clones for smooth looping
        autoScroll: {
          speed: normalizedSpeed,
          autoStart: true,
          pauseOnHover: false, // Disabled pause on hover as requested
          pauseOnFocus: true,
        },
        // Enable keyboard navigation
        keyboard: true,
        // Allow touch/mouse dragging without snapping
        drag: 'free',
        // Disable snapping behavior for free dragging
        flickPower: 0,
        flickMaxPages: 0,
        // Reduce motion for accessibility
        reducedMotion: {
          speed: normalizedSpeed * 0.5,
          rewindSpeed: normalizedSpeed * 0.5,
          autoScroll: {
            speed: normalizedSpeed * 0.5,
          },
        },
      };
      
      this.splide = new Splide(this, config);

      // Mount with AutoScroll extension using the CDN format
      this.splide.mount(window.splide.Extensions);
      
      // Add event listeners
      this.splide.on('mounted', () => {

      });
      
      this.splide.on('autoScroll:play', () => {
        
      });
      
      this.splide.on('autoScroll:pause', () => {
        
      });
      
      // Resume auto-scroll after dragging ends
      this.splide.on('dragged', () => {
        if (this.splide && this.splide.Components && this.splide.Components.AutoScroll) {
          // Small delay to ensure drag has fully completed
          setTimeout(() => {
            this.splide.Components.AutoScroll.play();
          }, 100);
        }
      });
      
      // Also resume after any move operation completes
      this.splide.on('moved', () => {
        if (this.splide && this.splide.Components && this.splide.Components.AutoScroll) {
          // Only resume if not currently playing
          if (this.splide.Components.AutoScroll.isPaused()) {
      setTimeout(() => {
              this.splide.Components.AutoScroll.play();
      }, 50);
    }
        }
      });
      
    } catch (error) {
      console.error('[AutoScrollGallery] Failed to initialize Splide:', error);
      // Try fallback
      this.initBasicSplide();
    }
  }

  openDialog(clickedIndex = 0) {
    if (!this.dialog) {
      return;
    }
    // Pause the auto-scrolling
    if (this.splide && this.splide.Components && this.splide.Components.AutoScroll) {
      this.splide.Components.AutoScroll.pause();
    }
    
    // Store current focus to restore later if needed
    const currentFocus = document.activeElement;
    
    // Show the dialog
    this.dialog.showModal();
    
    // Immediately blur any focused element to prevent Safari's default focus
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    
    // Initialize fullscreen splide
    this.initFullscreenSplide(clickedIndex);
    
    // Only trap focus and set initial focus if opened by keyboard
    if (this._keyboardOpen) {
      setTimeout(() => {
        const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
        const firstPaginationButton = paginationContainer?.querySelector('.pagination-number');
        if (firstPaginationButton) {
          if (typeof trapFocus === 'function') {
            trapFocus(this.dialog, firstPaginationButton);
          } else {
            firstPaginationButton.focus();
          }
        } else {
          // fallback: trap focus on dialog, but don't focus close button
          if (typeof trapFocus === 'function') {
            trapFocus(this.dialog, this.dialog);
          }
        }
      }, 0);
    } else {
      // For mouse clicks, just ensure focus trap without initial focus
      if (typeof trapFocus === 'function') {
        trapFocus(this.dialog);
      }
    }
    
    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
      Shopify.analytics.publish('image_gallery_opened');
    }
  }

  handleDialogFocusTrap(e) {
    if (e.key !== 'Tab') return;
    const dialog = e.currentTarget;
    const focusable = dialog.querySelectorAll('.fullscreen-carousel-dialog__close, .pagination-number');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  closeDialog(event) {
    if (!this.dialog) return;
    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
    this.dialog.close();
    // Return focus to trigger if opened by keyboard
    if (this._keyboardOpen && this._lastTrigger) {
      setTimeout(() => {
        this._lastTrigger.focus();
      }, 0);
    }
    // Resume the auto-scrolling
    if (this.splide && this.splide.Components && this.splide.Components.AutoScroll) {
      this.splide.Components.AutoScroll.play();
    } 
    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
      Shopify.analytics.publish('image_gallery_closed');
    }
  }

  handleDialogClose() {
    // Resume auto-scroll when dialog closes
    if (this.splide && this.splide.Components && this.splide.Components.AutoScroll) {
      this.splide.Components.AutoScroll.play();
    }
  }

  handleDialogClick(e) {
    // Don't close if clicking on pagination, navigation buttons, close button
    if (
      e.target.closest('.fullscreen-carousel__pagination') ||
      e.target.closest('.splide__pagination') ||
      e.target.closest('.fullscreen-carousel-dialog__close') ||
      e.target.closest('.splide__arrow')
    ) {
      e.stopPropagation();
      return;
    }

    // Close the dialog when clicking anywhere else
    this.closeDialog(e);
  }

  initFullscreenSplide(initialSlideIndex = 0) {
    const fullscreenCarousel = this.dialog.querySelector('.fullscreen-carousel');
    if (!fullscreenCarousel) {
      return;
    }

    // For fullscreen, we can use basic Splide even if AutoScroll isn't available
    if (typeof Splide === 'undefined') {
      return;
    }

    // Destroy existing splide if it exists
    if (this.fullscreenSplide) {
      this.fullscreenSplide.destroy();
    }

    // Different config for mobile vs desktop
    const config = {
      start: initialSlideIndex,
      pagination: true, // Enable built-in pagination
      arrows: false, // Disable arrows since we have pagination
      keyboard: true,
      drag: true,
      // No auto-scroll in fullscreen mode
    };

    if (this.isMobile) {
      // Mobile: disable carousel behavior for natural scrolling
      config.drag = 'free';
      config.wheel = true;
      config.keyboard = false;
      config.pagination = false; // Hide pagination on mobile
      config.type = 'slide';
      config.perPage = 1;
      config.direction = 'ttb';
      config.height = '100vh';
      config.snap = false; // Disable snapping for smoother scrolling
      config.flickPower = 0; // Disable flick behavior
      config.flickMaxPages = 0; // Prevent page jumping on flick
    } else {
      // Desktop: vertical carousel layout
      config.direction = 'ttb';
      config.height = '100vh';
      config.perPage = 1;
      config.keyboard = true; // Enable keyboard navigation
      config.wheel = true; // Re-enable built-in wheel handling
      config.releaseWheel = true;
      config.type = 'slide';
      config.pagination = false; // Disable built-in pagination for custom numbered version
      // Improve wheel sensitivity for trackpads
      config.wheelSleep = 300; // Longer delay between wheel events
      config.wheelMinThreshold = 20; // Higher threshold for trackpad inertia
      config.waitForTransition = true
    }

    try {
      this.fullscreenSplide = new Splide(fullscreenCarousel, config);

      // Track slide changes for analytics
      this.fullscreenSplide.on('moved', (newIndex) => {
        if (typeof Shopify !== 'undefined' && Shopify.analytics) {
          Shopify.analytics.publish('image_gallery_viewed', { 
            image_location: newIndex + 1 
          });
        }
      });

      // Set up custom numbered pagination after mounting
      this.fullscreenSplide.on('mounted', () => {
        this.setupNumberedPagination();
        // Set up arrow key navigation after splide is mounted
        this.setupModalArrowNavigation();
      });

      this.fullscreenSplide.mount();
      
    } catch (error) {
    }
  }

  setupNumberedPagination() {
    if (!this.fullscreenSplide) {
      console.warn('[AutoScrollGallery] No fullscreen splide for pagination');
      return;
    }

    const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
    if (!paginationContainer) {
      console.warn('[AutoScrollGallery] No pagination container found');
      return;
    }

    const totalSlides = this.fullscreenSplide.length;
    
    // Create numbered pagination HTML
    let paginationHTML = '';
    for (let i = 0; i < totalSlides; i++) {
      const formattedNumber = String(i + 1).padStart(2, '0');
      const activeClass = i === this.fullscreenSplide.index ? 'active' : '';
      paginationHTML += `<button class="pagination-number ${activeClass}" data-index="${i}" tabindex="0" aria-label="Go to slide ${i + 1}">${formattedNumber}</button>`;
    }
    paginationContainer.innerHTML = paginationHTML;

    // Add click events to pagination
    paginationContainer.addEventListener('click', (e) => {
      const target = e.target;
      if (target.classList.contains('pagination-number')) {
        const index = parseInt(target.dataset.index, 10);
        if (!isNaN(index) && this.fullscreenSplide) {
          this.fullscreenSplide.go(index);
        }
        e.stopPropagation();
      }
    });

    // Update active pagination on slide change
    this.fullscreenSplide.on('moved', (newIndex) => {
      const buttons = paginationContainer.querySelectorAll('.pagination-number');
      buttons.forEach((button, index) => {
        button.classList.toggle('active', index === newIndex);
        });
      });
  }
}

customElements.define('auto-scroll-gallery', AutoScrollGallery);